import {
  EditBase,
  Form,
  type Identifier,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";

import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { TaskDeleteAction } from "./TaskDeleteAction";

import { SaveButton } from "@/components/admin/form";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Task } from "../types";

import { TaskFormContent } from "./TaskFormContent";

type TaskContactLink = {
  id: Identifier;

  task_id: Identifier;

  contact_id: Identifier;

  created_at?: string;
};

export const TaskEdit = ({
  open,
  close,
  taskId,
  onDeleted,
}: {
  taskId: Identifier;

  open: boolean;

  close: () => void;
  onDeleted?: () => void;
}) => {
  const notify = useNotify();

  const translate = useTranslate();

  const dataProvider = useDataProvider();

  const queryClient = useQueryClient();

  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    [],
  );

  const [initialized, setInitialized] = useState(false);

  /*
   * Загружаем саму задачу.
   *
   * Это нужно в том числе
   * для совместимости со старым
   * tasks.contact_id.
   */
  const { data: task, isPending: isTaskPending } = useGetOne<Task>(
    "tasks",
    {
      id: taskId,
    },
    {
      enabled: open && taskId != null,
    },
  );

  /*
   * Все новые связи
   * контакт ↔ задача.
   */
  const {
    data: taskContacts = [],
    isPending: isContactsPending,
    refetch: refetchTaskContacts,
  } = useGetList<TaskContactLink>(
    "task_contacts",
    {
      pagination: {
        page: 1,
        perPage: 1000,
      },

      sort: {
        field: "created_at",
        order: "ASC",
      },

      filter: {
        task_id: taskId,
      },
    },
    {
      enabled: open && taskId != null,
    },
  );

  /*
   * При каждом новом открытии
   * разрешаем заново заполнить
   * выбранные контакты.
   */
  useEffect(() => {
    if (!open) {
      setInitialized(false);

      setSelectedContactIds([]);
    }
  }, [open, taskId]);

  /*
   * После загрузки связей
   * формируем текущий список.
   */
  useEffect(() => {
    if (!open || initialized || isTaskPending || isContactsPending || !task) {
      return;
    }

    const ids = new Set<Identifier>();

    taskContacts.forEach((relation) => {
      ids.add(relation.contact_id);
    });

    /*
     * Поддержка старых задач,
     * если по какой-то причине
     * contact_id ещё не был
     * перенесён в task_contacts.
     */
    if (task.contact_id != null) {
      ids.add(task.contact_id);
    }

    setSelectedContactIds(Array.from(ids));

    setInitialized(true);
  }, [open, initialized, isTaskPending, isContactsPending, task, taskContacts]);

  /*
   * После обычного UPDATE tasks
   * синхронизируем таблицу
   * task_contacts.
   */
  const syncTaskContacts = async () => {
    const desiredIds = new Set(selectedContactIds.map(String));

    const existingIds = new Set(
      taskContacts.map((relation) => String(relation.contact_id)),
    );

    /*
     * Связи, которые
     * нужно добавить.
     */
    const idsToAdd = selectedContactIds.filter(
      (contactId) => !existingIds.has(String(contactId)),
    );

    /*
     * Связи, которые
     * пользователь удалил.
     */
    const relationsToDelete = taskContacts.filter(
      (relation) => !desiredIds.has(String(relation.contact_id)),
    );

    const operations: Promise<any>[] = [];

    idsToAdd.forEach((contactId) => {
      operations.push(
        dataProvider.create("task_contacts", {
          data: {
            task_id: taskId,

            contact_id: contactId,
          },
        }),
      );
    });

    relationsToDelete.forEach((relation) => {
      operations.push(
        dataProvider.delete("task_contacts", {
          id: relation.id,

          previousData: relation,
        }),
      );
    });

    if (operations.length > 0) {
      await Promise.all(operations);
    }

    await refetchTaskContacts();

    /*
     * contact_tasks теперь
     * используется карточкой
     * контакта, поэтому обязательно
     * инвалидируем и его.
     */
    await queryClient.invalidateQueries({
      queryKey: ["contact_tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["task_contacts"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["contacts"],
    });
  };

  const handleUpdateSuccess = async () => {
    try {
      await syncTaskContacts();

      close();

      notify("Задача обновлена", {
        type: "success",
      });
    } catch (error) {
      console.error("Failed to sync task contacts", error);

      /*
       * Сама задача уже сохранена,
       * поэтому не говорим,
       * что сохранение полностью
       * провалилось.
       */
      close();

      notify("Задача сохранена, но возникла ошибка при обновлении контактов", {
        type: "warning",
      });
    }
  };

  const handleDeleteSuccess = async () => {
    /*
     * task_contacts удаляются
     * каскадно вместе с задачей.
     */
    await queryClient.invalidateQueries({
      queryKey: ["contact_tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["task_contacts"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["tasks"],
    });

    (onDeleted ?? close)();

    notify("Задача удалена", {
      type: "success",
    });
  };

  const handleDialogChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      close();
    }
  };

  const loading = isTaskPending || isContactsPending || !initialized;

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      {open && taskId != null && (
        <EditBase
          id={taskId}
          resource="tasks"
          className="mt-0"
          mutationMode="pessimistic"
          mutationOptions={{
            onSuccess: handleUpdateSuccess,
          }}
          redirect={false}
        >
          <DialogContent className="lg:max-w-xl overflow-y-auto max-h-[90vh]">
            {loading ? (
              <div className="py-8 text-sm text-muted-foreground">
                Загрузка...
              </div>
            ) : (
              <Form className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle>
                    {translate("resources.tasks.action.edit")}
                  </DialogTitle>
                </DialogHeader>

                <TaskFormContent
                  selectedContactIds={selectedContactIds}
                  onSelectedContactIdsChange={setSelectedContactIds}
                />

                <DialogFooter className="w-full sm:justify-between gap-4">
                  {task && (
                    <TaskDeleteAction
                      task={task}
                      onDeleted={handleDeleteSuccess}
                    />
                  )}

                  <SaveButton label="ra.action.save" />
                </DialogFooter>
              </Form>
            )}
          </DialogContent>
        </EditBase>
      )}
    </Dialog>
  );
};
